export const DEFAULT_API_PORT = 4000;
export const DEFAULT_WEB_PORT = 3000;
export const DEFAULT_DATABASE_NAME = "priceon";
export const DEFAULT_DATABASE_USER = "priceon_app";
export const REFERRAL_BONUS_LAMPORTS = 0n;
export const REFERRAL_DEPOSIT_UNLOCK_LAMPORTS = 0n;
export const REFERRAL_MAX_ACTIVATIONS = 999999;
/** The wall-clock cadence at which UTC rounds start. */
export const ROUND_CYCLE_MS = 60_000;
/** The scored market interval inside each cycle. */
export const ROUND_ACTIVE_MS = 45_000;
/** Backwards-compatible name for the active market duration. */
export const ROUND_DURATION_MS = ROUND_ACTIVE_MS;
export const PREDICTION_LEAD_MS = 15_000;
export const DEMO_INITIAL_BALANCE = "10.0000";
export const DEMO_MIN_STAKE = "0.01";
export const DEMO_FEE_BPS = 100;
export const DEMO_AMOUNT_DECIMALS = 4;
export function quoteDemoTrade(stakeSol) {
    if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(stakeSol))
        return undefined;
    const [whole] = stakeSol.split(".");
    if (whole.length > 16)
        return undefined;
    const stake = toDemoUnits(stakeSol, 2);
    if (stake === undefined || stake <= 0n)
        return undefined;
    const fee = (stake * BigInt(DEMO_FEE_BPS)) / 10000n;
    return {
        stakeSol: fromDemoUnits(stake),
        feeSol: fromDemoUnits(fee),
        totalCostSol: fromDemoUnits(stake + fee),
    };
}
export function getMaxDemoStake(availableBalance) {
    const available = toDemoUnits(availableBalance, DEMO_AMOUNT_DECIMALS);
    if (available === undefined || available <= 0n)
        return "0.0000";
    const costPerCent = 101n;
    const stakeCents = available / costPerCent;
    return fromDemoUnits(stakeCents * 100n);
}
function toDemoUnits(value, maximumDecimals) {
    if (!new RegExp(`^(?:0|[1-9]\\d*)(?:\\.\\d{1,${maximumDecimals}})?$`).test(value))
        return undefined;
    const [whole, fraction = ""] = value.split(".");
    return BigInt(whole) * 10000n + BigInt(fraction.padEnd(DEMO_AMOUNT_DECIMALS, "0"));
}
function fromDemoUnits(value) {
    const whole = value / 10000n;
    const fraction = String(value % 10000n).padStart(DEMO_AMOUNT_DECIMALS, "0");
    return `${whole}.${fraction}`;
}
export function getWinStreaks(outcomes) {
    let currentStreak = 0;
    let bestStreak = 0;
    for (const outcome of outcomes) {
        currentStreak = outcome === "won" ? currentStreak + 1 : 0;
        bestStreak = Math.max(bestStreak, currentStreak);
    }
    return { currentStreak, bestStreak };
}
export const LIVE_FEE_BPS = 100;
export const LIVE_MIN_STAKE_LAMPORTS = 10000000n;
/** Maximum live stake is this fraction of the game reserve, in basis points. */
export const LIVE_MAX_STAKE_RESERVE_BPS = 500;
export function quoteLiveTrade(stakeSol) {
    const stake = parseLiveSol(stakeSol);
    if (stake === undefined || stake <= 0n)
        return undefined;
    const fee = (stake * BigInt(LIVE_FEE_BPS)) / 10000n;
    return {
        stakeLamports: stake.toString(),
        stakeSol: formatLiveLamports(stake),
        feeLamports: fee.toString(),
        feeSol: formatLiveLamports(fee),
        totalCostLamports: (stake + fee).toString(),
        totalCostSol: formatLiveLamports(stake + fee),
    };
}
export function maxLiveStakeFromReserve(reserveLamports) {
    if (reserveLamports <= 0n)
        return 0n;
    return (reserveLamports * BigInt(LIVE_MAX_STAKE_RESERVE_BPS)) / 10000n;
}
export function getMaxLiveStake(availableLamports, maximumStakeLamports) {
    if (!/^\d+$/.test(availableLamports) || !/^\d+$/.test(maximumStakeLamports))
        return "0";
    const available = BigInt(availableLamports);
    let low = 0n;
    let high = available < BigInt(maximumStakeLamports) ? available : BigInt(maximumStakeLamports);
    while (low < high) {
        const candidate = (low + high + 1n) / 2n;
        const fee = (candidate * BigInt(LIVE_FEE_BPS)) / 10000n;
        if (candidate + fee <= available)
            low = candidate;
        else
            high = candidate - 1n;
    }
    return formatLiveLamports(low);
}
function parseLiveSol(value) {
    if (!/^(?:0|[1-9]\d{0,19})(?:\.\d{1,9})?$/.test(value))
        return undefined;
    const [whole, fraction = ""] = value.split(".");
    return BigInt(whole) * 1000000000n + BigInt(fraction.padEnd(9, "0"));
}
function formatLiveLamports(value) {
    const whole = value / 1000000000n;
    const fraction = String(value % 1000000000n).padStart(9, "0").replace(/0+$/, "");
    return `${whole}${fraction ? `.${fraction}` : ""}`;
}
export const COMPETITION_FEE_BPS = 1_000;
export const COMPETITION_ENTRY_LEAD_MS = 60_000;
/** Kept for consumers that still import the old name; first picks use the standard decision window. */
export const COMPETITION_FIRST_PICK_LEAD_MS = PREDICTION_LEAD_MS;
const COMPETITION_STAKES = {
    5: "0.1000",
    10: "0.2000",
    15: "0.4000",
};
export function getCompetitionSpec(durationMinutes) {
    const entryStakeSol = COMPETITION_STAKES[durationMinutes];
    const stake = toDemoUnits(entryStakeSol, DEMO_AMOUNT_DECIMALS);
    const fee = (stake * BigInt(COMPETITION_FEE_BPS)) / 10000n;
    return {
        durationMinutes,
        roundCount: durationMinutes,
        entryStakeSol,
        feeSol: fromDemoUnits(fee),
        totalCostSol: fromDemoUnits(stake + fee),
    };
}
export const SOLANA_ASSET = "SOL";
export const SOLANA_LAMPORTS_PER_SOL = 1000000000n;
export const SOLANA_SWEEP_THRESHOLD_LAMPORTS = 10000000n;
export const SOLANA_WITHDRAWAL_MIN_LAMPORTS = 10000000n;
export const SOLANA_WITHDRAWAL_AUTO_PROFIT_24H_LAMPORTS = 1000000000n;
/** @deprecated Withdrawal requests are limited by available balance and review policy. */
export const SOLANA_WITHDRAWAL_MAX_LAMPORTS = 1000000000n;
/** @deprecated Use SOLANA_WITHDRAWAL_AUTO_PROFIT_24H_LAMPORTS. */
export const SOLANA_WITHDRAWAL_REVIEW_PROFIT_24H_LAMPORTS = 5000000000n;
/** @deprecated The rolling hard rejection limit is no longer enforced. */
export const SOLANA_WITHDRAWAL_HARD_LIMIT_24H_LAMPORTS = 25000000000n;
// Short aliases are useful to backend workers that deal directly in lamports.
export const LAMPORTS_PER_SOL = SOLANA_LAMPORTS_PER_SOL;
export const DEFAULT_SWEEP_THRESHOLD_LAMPORTS = SOLANA_SWEEP_THRESHOLD_LAMPORTS;
export const ADMIN_SESSION_HEADER = "x-priceon-admin-session";
